/**
 * GraphQL Tracking Mutation Hooks
 *
 * Hooks for activity tracking operations: batch track, update checkpoint.
 */

import { useMutation, gql } from '@apollo/client';
import logger from '../../utils/logger';

// GraphQL Mutations
const TRACK_ACTIVITIES_MUTATION = gql`
  mutation TrackActivities($input: BatchTrackActivitiesInput!) {
    trackActivities(input: $input)
  }
`;

const UPDATE_CHECKPOINT_MUTATION = gql`
  mutation UpdateCheckpoint($input: UpdateCheckpointInput!) {
    updateCheckpoint(input: $input)
  }
`;

// Types
interface ActivityInput {
  activityType: string;
  targetType: string;
  targetId: string;
  metadata?: Record<string, unknown>;
}

interface ActivityPayload {
  id: string;
  userId: string;
  activityType: string;
  targetType: string;
  targetId: string;
  firstOccurredAt: string;
  lastOccurredAt: string;
  occurrenceCount: number;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

interface TrackActivitiesPayload {
  success: boolean;
  activities: ActivityPayload[];
  count: number;
}

interface CheckpointPayload {
  userId: string;
  context: string;
  lastCheckedAt: string;
}

/**
 * Hook for batch tracking activities
 */
export function useTrackActivitiesGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    { trackActivities: TrackActivitiesPayload },
    { input: { activities: ActivityInput[] } }
  >(TRACK_ACTIVITIES_MUTATION, {
    onCompleted: (data) => {
      logger.debug('Tracking', 'Activities tracked via GraphQL', {
        count: data.trackActivities.count,
      });
    },
    onError: (err) => {
      logger.error('Tracking', 'Failed to track activities', err);
    },
  });

  const trackActivities = async (activities: ActivityInput[]) => {
    const result = await mutate({
      variables: { input: { activities } },
    });
    return result.data?.trackActivities;
  };

  return { trackActivities, loading, error };
}

/**
 * Hook for updating user checkpoint
 */
export function useUpdateCheckpointGraphQL() {
  const [mutate, { loading, error }] = useMutation<
    { updateCheckpoint: CheckpointPayload },
    { input: { context: string } }
  >(UPDATE_CHECKPOINT_MUTATION, {
    onCompleted: (data) => {
      logger.debug('Tracking', 'Checkpoint updated via GraphQL', {
        context: data.updateCheckpoint.context,
      });
    },
    onError: (err) => {
      logger.error('Tracking', 'Failed to update checkpoint', err);
    },
  });

  const updateCheckpoint = async (context: string) => {
    const result = await mutate({
      variables: { input: { context } },
    });
    return result.data?.updateCheckpoint;
  };

  return { updateCheckpoint, loading, error };
}
